import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPageLayout, Section, Card, Button } from '../../components';
import { ListingActionBar } from '../../components/listing/v2';
import { CreateAction } from '../../components/listing/v2/ListingAction';
import DocumentTemplateList from '../../components/club-documents/DocumentTemplateList';
import clubDocumentApi from '../../services/clubDocumentApi';
import apiService from '../../services/api';

const DocumentTemplates = ({ scope = 'auto' }) => {
  const navigate = useNavigate();
  const [clubData, setClubData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [detectedScope, setDetectedScope] = useState(null);

  const loadClubData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const userInfo = await apiService.getCurrentUser();
      const isPlatformAdmin = userInfo.is_platform_admin || userInfo.primary_role === 'platform_admin';
      const club_id = userInfo.club_id;

      if (scope === 'auto') {
        if (isPlatformAdmin && !club_id) {
          setDetectedScope('platform');
          setClubData({ id: 'platform', name: 'Platform Templates' });
        } else if (club_id) {
          setDetectedScope('club');
          try {
            const clubResponse = await apiService.get('/api/clubs/public/club-info');
            setClubData({
              id: club_id,
              name: clubResponse.name || clubResponse.display_name || 'Club Name',
              club_id: club_id
            });
          } catch (err) {
            setClubData({ id: club_id, name: 'Club Entity', club_id: club_id });
          }
        } else {
          setError('No operational context available for document management');
        }
      } else {
        setDetectedScope(scope);
        if (scope === 'platform') {
          setClubData({ id: 'platform', name: 'Platform Templates' });
        } else if (club_id) {
          try {
            const clubResponse = await apiService.get('/api/clubs/public/club-info');
            setClubData({
              id: club_id,
              name: clubResponse.name || clubResponse.display_name || 'Club Name',
              club_id: club_id
            });
          } catch (err) {
            setClubData({ id: club_id, name: 'Club Entity', club_id: club_id });
          }
        }
      }
    } catch (err) {
      setError(`Context synchronisation failure: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    loadClubData();
  }, [loadClubData]);

  const handleEditTemplate = (template) => {
    const basePath = detectedScope === 'platform' ? '/admin/platform/document-templates' : '/admin/club/document-templates';
    navigate(`${basePath}/${template.id}/edit`);
    setRefreshTrigger(prev => prev + 1);
  };

  const handleCreateTemplate = () => {
    const basePath = detectedScope === 'platform' ? '/admin/platform/document-templates' : '/admin/club/document-templates';
    navigate(`${basePath}/create`);
  };

  if (loading) {
    return (
      <AdminPageLayout title="DOCUMENT ARCHITECTURE" maxWidth="1400px">
        <div style={{ padding: '4rem', textAlign: 'center', opacity: 0.6 }}>Synchronizing template repository...</div>
      </AdminPageLayout>
    );
  }

  if (error) {
    return (
      <AdminPageLayout title="DOCUMENT ARCHITECTURE" maxWidth="1400px">
        <Section title="OPERATIONAL ERROR">
          <Card level={1} variant="error">
            <div style={{ padding: '2rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--color-error)' }}>CONTEXT SYNCHRONISATION FAILURE</div>
              <p style={{ marginBottom: '2rem', opacity: 0.8 }}>{error}</p>
              <Button onClick={loadClubData} variant="primary">RETRY SYNCHRONISATION</Button>
            </div>
          </Card>
        </Section>
      </AdminPageLayout>
    );
  }

  const pageTitle = detectedScope === 'platform' ? 'PLATFORM DOCUMENT ARCHITECTURE' : 'DOCUMENT ARCHITECTURE';
  const pageSubtitle = detectedScope === 'platform'
    ? 'Standardized compliance frameworks for global deployment'
    : `Provisioning document requirements for ${clubData?.name || 'Club Entity'}`;
  const backPath = detectedScope === 'platform' ? '/admin/system-templates' : '/admin/documents';
  const backLabel = detectedScope === 'platform' ? '← Back to System Templates' : '← Back to Documents';

  return (
    <AdminPageLayout
      title={pageTitle}
      subtitle={pageSubtitle}
      backPath={backPath}
      maxWidth="1400px"
    >
      <Section>
        <div style={{ marginBottom: '1.5rem' }}>
          <ListingActionBar
            rightSection={
              <CreateAction onClick={handleCreateTemplate} label="New Document Template" />
            }
          />
        </div>
        <Card level={1}>
          {clubData && (
            <DocumentTemplateList
              key={refreshTrigger}
              club_id={clubData.id}
              showActions={true}
              isAdmin={true}
              onEditTemplate={handleEditTemplate}
              onCreateTemplate={handleCreateTemplate}
            />
          )}
        </Card>
      </Section>
    </AdminPageLayout>
  );
};

export default DocumentTemplates;
